import { encodeFunctionData, getAddress, maxUint256 } from "viem";
import { SYNDICATE_VAULT_ABI, ERC20_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { usage } from "../errors.js";
/**
 * Vault `agentFeeBps` bounds in basis points, mirroring the on-chain
 * `FeeConstants` library: `MAX_AGENT_FEE_BPS` is the vault's hard cap (15%) and
 * `DEFAULT_AGENT_FEE_BPS` is the getter's unset fallback (5%). Exported so
 * consumers and the encode-time guard share one searchable source rather than a
 * bare literal (PR #384 review pass 3).
 */
export const MAX_AGENT_FEE_BPS = 1500n;
export const DEFAULT_AGENT_FEE_BPS = 500n;
/** Minimal WETH9 `deposit()` (payable) fragment — for the ETH→WETH wrap leg. */
const WETH_ABI = [
    {
        name: "deposit",
        type: "function",
        stateMutability: "payable",
        inputs: [],
        outputs: [],
    },
];
const ZERO_VALUE = "0x0";
function toHexValue(value) {
    return `0x${value.toString(16)}`;
}
function encodeApproveTx(token, spender, amount, chainId) {
    return {
        to: token,
        data: encodeFunctionData({
            abi: ERC20_ABI,
            functionName: "approve",
            args: [spender, amount],
        }),
        value: ZERO_VALUE,
        chainId,
    };
}
function encodeDepositTx(vault, assets, receiver, chainId) {
    return {
        to: vault,
        data: encodeFunctionData({
            abi: SYNDICATE_VAULT_ABI,
            functionName: "deposit",
            args: [assets, receiver],
        }),
        value: ZERO_VALUE,
        chainId,
    };
}
/**
 * Encode the calldata for an ERC-4626 vault deposit.
 *
 * Returns 1–2 transactions:
 *   - If `currentAllowance` is provided and >= `assets`, only the `deposit` tx.
 *   - Otherwise, an `approve(spender=vault, amount=assets)` tx followed by the
 *     `deposit` tx. Agents that already have an unlimited allowance can drop
 *     the first tx after checking the `allowance` precondition.
 *
 * The SDK never reads chain state — pass `currentAllowance` if you want a
 * precise plan; otherwise expect the conservative two-tx default.
 */
export function encodeDeposit(args, chainId) {
    if (args.assets <= 0n) {
        throw usage("`assets` must be > 0");
    }
    const vault = getAddress(args.vault);
    const receiver = getAddress(args.receiver);
    const asset = getAddress(args.asset);
    const txs = [];
    // Optional ETH→WETH wrap leg (WETH vaults only). Prepended so the wrap lands
    // before the approve/deposit. Wraps only the shortfall when a balance is given.
    let wrapped = false;
    if (args.wrapEth) {
        const weth = getDeployment(chainId).tokens.weth;
        if (!weth || getAddress(weth.address) !== asset) {
            throw usage("`wrapEth` is only valid for WETH vaults — the vault asset must be the chain's WETH.");
        }
        const held = args.currentWethBalance ?? 0n;
        const shortfall = held >= args.assets ? 0n : args.assets - held;
        if (shortfall > 0n) {
            txs.push({
                to: asset,
                data: encodeFunctionData({ abi: WETH_ABI, functionName: "deposit", args: [] }),
                value: toHexValue(shortfall),
                chainId,
            });
            wrapped = true;
        }
    }
    const needsApprove = args.currentAllowance === undefined || args.currentAllowance < args.assets;
    if (needsApprove) {
        txs.push(encodeApproveTx(asset, vault, maxUint256, chainId));
    }
    txs.push(encodeDepositTx(vault, args.assets, receiver, chainId));
    const minDecimal = formatUnits(args.assets, args.assetDecimals);
    let description;
    if (wrapped) {
        description = needsApprove
            ? `Wrap ETH→WETH, approve ${args.assetSymbol} for ${vault}, then deposit ${minDecimal} ${args.assetSymbol}.`
            : `Wrap ETH→WETH, then deposit ${minDecimal} ${args.assetSymbol}.`;
    }
    else {
        description = needsApprove
            ? `Approve ${args.assetSymbol} for ${vault}, then deposit ${minDecimal} ${args.assetSymbol}.`
            : `Deposit ${minDecimal} ${args.assetSymbol} into ${vault}.`;
    }
    return {
        txs,
        preconditions: [
            {
                type: "balance",
                asset,
                assetSymbol: args.assetSymbol,
                min: args.assets.toString(),
                minDecimal,
            },
            {
                type: "allowance",
                token: asset,
                spender: vault,
                min: args.assets.toString(),
                minDecimal,
            },
            { type: "vault-not-locked", vault },
            { type: "depositor-approved", vault, depositor: receiver },
        ],
        description,
        note: "Both txs are gated by vault state. If `redemptionsLocked()` is true the deposit reverts unless live NAV is available; check the vault read endpoint before broadcasting.",
    };
}
/** Internal: decimal-string formatter so we don't pull in a heavy dep. */
function formatUnits(value, decimals) {
    if (decimals === 0)
        return value.toString();
    const negative = value < 0n;
    const abs = negative ? -value : value;
    const str = abs.toString().padStart(decimals + 1, "0");
    const head = str.slice(0, str.length - decimals);
    const tail = str.slice(str.length - decimals).replace(/0+$/, "");
    const out = tail.length > 0 ? `${head}.${tail}` : head;
    return negative ? `-${out}` : out;
}
// Re-export for tests / callers that want to pre-format previews.
export { formatUnits };
// Tiny helpers exported for the API layer to convert plan-level UnsignedTx
// into the on-the-wire JSON shape with a hex `value`. Kept here so encoders
// own their wire representation end to end.
export { toHexValue };
export function encodeRedeem(args, chainId) {
    if (args.shares <= 0n)
        throw usage("`shares` must be > 0");
    const vault = getAddress(args.vault);
    const receiver = getAddress(args.receiver);
    const owner = getAddress(args.owner);
    return {
        txs: [
            {
                to: vault,
                data: encodeFunctionData({
                    abi: SYNDICATE_VAULT_ABI,
                    functionName: "redeem",
                    args: [args.shares, receiver, owner],
                }),
                value: ZERO_VALUE,
                chainId,
            },
        ],
        preconditions: [
            { type: "vault-not-locked", vault },
        ],
        description: `Redeem ${args.shares.toString()} shares of ${vault} → ${receiver}.`,
        note: "Synchronous path. Reverts if the vault has an active proposal AND live NAV is unavailable; in that case use /prepare/request-redeem (queue path) instead.",
    };
}
export function encodeRequestRedeem(args, chainId) {
    if (args.shares <= 0n)
        throw usage("`shares` must be > 0");
    const vault = getAddress(args.vault);
    const owner = getAddress(args.owner);
    return {
        txs: [
            {
                to: vault,
                data: encodeFunctionData({
                    abi: SYNDICATE_VAULT_ABI,
                    functionName: "requestRedeem",
                    args: [args.shares, owner],
                }),
                value: ZERO_VALUE,
                chainId,
            },
        ],
        preconditions: [],
        description: `Queue a redeem request for ${args.shares.toString()} shares of ${vault}.`,
        note: "Only callable while `redemptionsLocked() == true`. Shares are escrowed in the per-vault queue; claim with the queue's `claim(requestId)` once the proposal settles.",
    };
}
// ── Owner-side admin encoders ─────────────────────────────
export function encodeApproveDepositor(args, chainId) {
    const vault = getAddress(args.vault);
    const depositor = getAddress(args.depositor);
    return {
        txs: [
            {
                to: vault,
                data: encodeFunctionData({
                    abi: SYNDICATE_VAULT_ABI,
                    functionName: "approveDepositor",
                    args: [depositor],
                }),
                value: ZERO_VALUE,
                chainId,
            },
        ],
        preconditions: [],
        description: `Approve depositor ${depositor} on vault ${vault}.`,
        note: "Vault owner only.",
    };
}
export function encodeSetAgentFeeBps(args, chainId) {
    const vault = getAddress(args.vault);
    const bps = typeof args.bps === "bigint" ? args.bps : BigInt(args.bps);
    // Client-side fail-fast against the same ceiling the vault enforces.
    if (bps < 0n || bps > MAX_AGENT_FEE_BPS) {
        throw usage("`bps` must be an integer in [0, 1500] (max 15%)");
    }
    return {
        txs: [
            {
                to: vault,
                data: encodeFunctionData({
                    abi: SYNDICATE_VAULT_ABI,
                    functionName: "setAgentFeeBps",
                    args: [bps],
                }),
                value: ZERO_VALUE,
                chainId,
            },
        ],
        preconditions: [],
        description: `Set agent performance fee to ${bps.toString()} bps on vault ${vault}.`,
        note: "Vault owner only. Capped at 1500 bps (15%, the vault's MAX_AGENT_FEE_BPS); the governor further clamps the realized fee to its configured maxPerformanceFeeBps at settlement, which may be lower.",
    };
}
export function encodeRegisterAgent(args, chainId) {
    const vault = getAddress(args.vault);
    const agentAddress = getAddress(args.agentAddress);
    const id = typeof args.agentId === "bigint" ? args.agentId : BigInt(args.agentId);
    if (id < 0n)
        throw usage("`agentId` must be a non-negative integer");
    return {
        txs: [
            {
                to: vault,
                data: encodeFunctionData({
                    abi: SYNDICATE_VAULT_ABI,
                    functionName: "registerAgent",
                    args: [agentAddress, id],
                }),
                value: ZERO_VALUE,
                chainId,
            },
        ],
        preconditions: [],
        description: `Register agent ${agentAddress} (id ${id.toString()}) on vault ${vault}.`,
        note: "Vault owner only. The ERC-8004 NFT must be owned by `agentAddress` or by the vault owner at registration time.",
    };
}
